/**
 * ShortcutHelp — per docs/specs/README.md
 * Overlay surfaced by `?` that documents the palette + chord shortcuts.
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-19-v1
 */
import { useEffect } from 'react';
import { X } from 'lucide-react';
import { ROUTES } from '../../lib/routes';
import { NAV_CHORDS } from '../hooks/use-keyboard-shortcuts';

interface ShortcutRow {
    keys: string[];
    label: string;
}

const GLOBAL_ROWS: ShortcutRow[] = [
    { keys: ['\u2318', 'K'], label: 'Open command palette' },
    { keys: ['/'], label: 'Focus topbar search' },
    { keys: ['?'], label: 'Show this help' },
    { keys: ['Esc'], label: 'Close palette / overlay' }
];

export function ShortcutHelp({ open, onClose }: { open: boolean; onClose: () => void }) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    // Build the chord table from the authoritative NAV_CHORDS map, resolving
    // each destination path back to its ROUTES entry for the label.
    const chordRows: ShortcutRow[] = Object.entries(NAV_CHORDS).map(([key, path]) => {
        const route = ROUTES.find((r) => r.path === path);
        return {
            keys: ['g', key],
            label: route ? `Go to ${route.label}` : `Go to ${path}`
        };
    });

    return (
        <div className="shortcut-help-backdrop" onClick={onClose} role="presentation">
            <div
                className="shortcut-help"
                role="dialog"
                aria-modal="true"
                aria-label="Keyboard shortcuts"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="shortcut-help-header">
                    <h2 className="shortcut-help-title">Keyboard shortcuts</h2>
                    <button
                        type="button"
                        className="shortcut-help-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </header>

                <div className="shortcut-help-body">
                    <section className="shortcut-help-section">
                        <h3 className="shortcut-help-section-title">Global</h3>
                        <ul className="shortcut-help-list">
                            {GLOBAL_ROWS.map((r) => (
                                <li key={r.label} className="shortcut-help-row">
                                    <span className="shortcut-help-label">{r.label}</span>
                                    <span className="shortcut-help-keys">
                                        {r.keys.map((k, i) => (
                                            <kbd key={`${r.label}-${i}`} className="palette-kbd">
                                                {k}
                                            </kbd>
                                        ))}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <section className="shortcut-help-section">
                        <h3 className="shortcut-help-section-title">Go to&hellip;</h3>
                        <ul className="shortcut-help-list shortcut-help-list-2col">
                            {chordRows.map((r) => (
                                <li key={r.label} className="shortcut-help-row">
                                    <span className="shortcut-help-label">{r.label}</span>
                                    <span className="shortcut-help-keys">
                                        {r.keys.map((k, i) => (
                                            <kbd key={`${r.label}-${i}`} className="palette-kbd">
                                                {k}
                                            </kbd>
                                        ))}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </section>
                </div>
            </div>
        </div>
    );
}
